import { supabase } from '@/integrations/supabase/client';
import { useQuery } from '@tanstack/react-query';
import { useState, useEffect } from 'react';
import { safeFormat } from '@/lib/safeDate';
import { useNavigate } from 'react-router-dom';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { parseDateSafe } from '@/lib/timezoneUtils';
import {
  Trash2,
  CheckCircle,
  Clock,
  User,
  Calendar,
  ExternalLink,
  Loader2,
  AlertTriangle,
  FileText,
  MessageSquare,
  Briefcase,
  Phone,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import type { FilteredTask } from '@/hooks/useFilteredTasks';
import { useEscalationContacts } from '@/hooks/useEscalationContacts';

interface TaskDetailSheetProps {
  task: FilteredTask | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  users: Array<{ id: string; display_name?: string | null; email?: string | null }>;
  onUpdate: (id: string, data: Partial<FilteredTask>) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
  onComplete: (task: FilteredTask) => void;
  isUpdating?: boolean;
  isDeleting?: boolean;
  /** Destaca a resposta da escalação (aberto pela notificação "Resposta recebida") */
  highlightResponse?: boolean;
}

const STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  pending: { label: 'Pendente', color: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300' },
  in_progress: { label: 'Em Progresso', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' },
  completed: { label: 'Concluída', color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' },
  blocked: { label: 'Bloqueada', color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' },
};

const PRIORITY_CONFIG: Record<string, { label: string; color: string }> = {
  low: { label: 'Baixa', color: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400' },
  medium: { label: 'Média', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' },
  high: { label: 'Alta', color: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300' },
  urgent: { label: 'Urgente', color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' },
};

const TASK_TYPE_CONFIG: Record<string, { label: string; emoji: string; color: string }> = {
  whatsapp: { label: 'WhatsApp', emoji: '💬', color: 'bg-green-50 text-green-700 border-green-200 dark:bg-green-950/30 dark:text-green-300 dark:border-green-800' },
  call: { label: 'Ligação', emoji: '📞', color: 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/30 dark:text-blue-300 dark:border-blue-800' },
  task: { label: 'Tarefa', emoji: '✅', color: 'bg-slate-50 text-slate-700 border-slate-200 dark:bg-slate-950/30 dark:text-slate-300 dark:border-slate-800' },
  lembrete: { label: 'Lembrete', emoji: '🔔', color: 'bg-yellow-50 text-yellow-700 border-yellow-200 dark:bg-yellow-950/30 dark:text-yellow-300 dark:border-yellow-800' },
  meeting_video: { label: 'Reunião Video', emoji: '🎥', color: 'bg-indigo-50 text-indigo-700 border-indigo-200 dark:bg-indigo-950/30 dark:text-indigo-300 dark:border-indigo-800' },
};

export function TaskDetailSheet({
  task,
  open,
  onOpenChange,
  users,
  onUpdate,
  onDelete,
  onComplete,
  isUpdating = false,
  isDeleting = false,
  highlightResponse = false,
}: TaskDetailSheetProps) {
  const { data: escalationResponse } = useQuery({
    queryKey: ['task-escalation-response', task?.id],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('session_escalations')
        .select('response, responded_at, responder:app_users!session_escalations_escalated_to_fkey(display_name)')
        .eq('task_id', task!.id)
        .not('responded_at', 'is', null)
        .order('responded_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return data as { response: string | null; responded_at: string; responder?: { display_name?: string | null } | null } | null;
    },
    enabled: open && highlightResponse && !!task?.id,
  });
  const navigate = useNavigate();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [editedNotes, setEditedNotes] = useState('');
  const [editedStatus, setEditedStatus] = useState('');
  const [editedPriority, setEditedPriority] = useState('');
  const [editedAssignee, setEditedAssignee] = useState('');
  const [hasChanges, setHasChanges] = useState(false);

  // Constant for unassigned value (Radix Select doesn't allow empty string)
  const UNASSIGNED_VALUE = '__unassigned__';

  // Reset form when task changes
  useEffect(() => {
    if (task) {
      setEditedNotes(task.notes || '');
      setEditedStatus(task.status);
      setEditedPriority(task.priority);
      setEditedAssignee(task.assigned_to || UNASSIGNED_VALUE);
      setHasChanges(false);
    }
  }, [task]);

  // Track changes
  useEffect(() => {
    if (task) {
      const currentAssignee = task.assigned_to || UNASSIGNED_VALUE;
      const changed =
        editedNotes !== (task.notes || '') ||
        editedStatus !== task.status ||
        editedPriority !== task.priority ||
        editedAssignee !== currentAssignee;
      setHasChanges(changed);
    }
  }, [task, editedNotes, editedStatus, editedPriority, editedAssignee]);

  const escalationSessionId = task?.metadata?.source === 'cs_escalation'
    ? (task?.metadata?.session_id as string | undefined) || null
    : null;
  const escalationContacts = useEscalationContacts(escalationSessionId ? [escalationSessionId] : []);
  const escalationContact = escalationSessionId ? escalationContacts[escalationSessionId] : null;

  if (!task) return null;

  const isCompleted = task.status === 'completed';
  const statusConfig = STATUS_CONFIG[task.status] || STATUS_CONFIG.pending;
  const priorityConfig = PRIORITY_CONFIG[task.priority] || PRIORITY_CONFIG.medium;
  const isEscalation = task.metadata?.source === 'cs_escalation';
  const isKommoReview = task.metadata?.type === 'kommo_review';
  const taskTypeKey = task.metadata?.type as string | undefined;
  const taskTypeConfig = taskTypeKey && !isKommoReview ? TASK_TYPE_CONFIG[taskTypeKey] : null;

  const getAssigneeName = () => {
    if (task.assigned_user_name) return task.assigned_user_name;
    if (task.assigned_user_first_name || task.assigned_user_last_name) {
      return `${task.assigned_user_first_name || ''} ${task.assigned_user_last_name || ''}`.trim();
    }
    const user = users.find((u) => u.id === task.assigned_to);
    return user?.display_name || user?.email || 'Não atribuído';
  };

  const handleSaveChanges = async () => {
    const updates: Partial<FilteredTask> = {};
    if (editedNotes !== (task.notes || '')) updates.notes = editedNotes;
    if (editedStatus !== task.status) {
      updates.status = editedStatus;
      if (editedStatus === 'completed') {
        updates.completed_at = new Date().toISOString();
      }
    }
    if (editedPriority !== task.priority) updates.priority = editedPriority;
    const currentAssignee = task.assigned_to || UNASSIGNED_VALUE;
    if (editedAssignee !== currentAssignee) {
      updates.assigned_to = editedAssignee === UNASSIGNED_VALUE ? null : editedAssignee;
    }

    if (Object.keys(updates).length > 0) {
      await onUpdate(task.id, updates);
    }
  };

  const handleComplete = async () => {
    if (isEscalation) {
      onComplete(task);
    } else {
      await onUpdate(task.id, {
        status: 'completed',
        completed_at: new Date().toISOString(),
      });
      onOpenChange(false);
    }
  };

  const handleDelete = async () => {
    await onDelete(task.id);
    setShowDeleteDialog(false);
    onOpenChange(false);
  };

  const handleGoToSession = () => {
    if (task.metadata?.session_id) {
      navigate(`/customer-service?session=${task.metadata.session_id}`);
      onOpenChange(false);
    }
  };

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className="sm:max-w-lg overflow-y-auto">
          <SheetHeader className="pb-4">
            <div className="flex flex-wrap gap-2 mb-2">
              <Badge className={cn('rounded-full', statusConfig.color)}>
                {statusConfig.label}
              </Badge>
              <Badge className={cn('rounded-full', priorityConfig.color)}>
                {priorityConfig.label}
              </Badge>
              {isEscalation && (
                <Badge className="rounded-full bg-orange-50 text-orange-700 border-orange-200 dark:bg-orange-950/30 dark:text-orange-300 dark:border-orange-800">
                  🔺 Escalação CS
                </Badge>
              )}
              {isKommoReview && (
                <Badge className="rounded-full bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-950/30 dark:text-purple-300 dark:border-purple-800">
                 📋 Revisão Kommo
                </Badge>
              )}
              {taskTypeConfig && (
                <Badge className={cn('rounded-full', taskTypeConfig.color)}>
                  {taskTypeConfig.emoji} {taskTypeConfig.label}
                </Badge>
              )}
            </div>
            <SheetTitle className="text-xl font-bold text-left">
              {task.title}
            </SheetTitle>
          </SheetHeader>

          {highlightResponse && escalationResponse && (
            <div className="mb-6 rounded-2xl border border-emerald-300 bg-emerald-50 p-4 ring-2 ring-emerald-400/40 animate-in fade-in slide-in-from-top-2 dark:border-emerald-800 dark:bg-emerald-950/30">
              <div className="mb-2 flex flex-wrap items-center gap-2">
                <Badge className="rounded-full bg-emerald-600 text-white hover:bg-emerald-600">✓ Resposta recebida</Badge>
                <span className="text-xs text-emerald-800 dark:text-emerald-300">
                  {escalationResponse.responder?.display_name ? `${escalationResponse.responder.display_name} · ` : ''}
                  {new Date(escalationResponse.responded_at).toLocaleString('pt-BR')}
                </span>
              </div>
              <p className="whitespace-pre-wrap text-sm text-emerald-950 dark:text-emerald-100">
                {escalationResponse.response || 'Sem texto de resposta.'}
              </p>
            </div>
          )}

          <div className="space-y-6">
            {/* Description */}
            {task.description && (
              <div className="space-y-2">
                <Label className="text-xs font-semibold text-muted-foreground uppercase">
                  Descrição
                </Label>
                <p className="text-sm text-foreground whitespace-pre-wrap">
                  {task.description}
                </p>
              </div>
            )}

            {/* Escalation Metadata */}
            {isEscalation && task.metadata && (
              <>
                <Separator />
                <div className="space-y-3 p-4 bg-orange-50 dark:bg-orange-950/20 rounded-lg border border-orange-200 dark:border-orange-800">
                  <div className="flex items-center gap-2 text-orange-700 dark:text-orange-300">
                    <AlertTriangle className="h-4 w-4" />
                    <span className="text-sm font-semibold">Detalhes da Escalação</span>
                  </div>

                  {escalationContact?.contactName && (
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">Cliente</Label>
                      {escalationContact.contactId ? (
                        <button
                          type="button"
                          onClick={() => {
                            navigate(`/contacts/${escalationContact.contactId}`);
                            onOpenChange(false);
                          }}
                          className="text-sm font-medium text-primary hover:underline flex items-center gap-1"
                        >
                          <User className="h-3.5 w-3.5" />
                          {escalationContact.contactName}
                        </button>
                      ) : (
                        <p className="text-sm font-medium flex items-center gap-1">
                          <User className="h-3.5 w-3.5" />
                          {escalationContact.contactName}
                        </p>
                      )}
                    </div>
                  )}
                  
                  
                  {task.metadata.question && (
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">Pergunta</Label>
                      <p className="text-sm">{String(task.metadata.question)}</p>
                    </div>
                  )}

                  {task.metadata.session_id && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full rounded-full"
                      onClick={handleGoToSession}
                    >
                      <ExternalLink className="h-4 w-4 mr-2" />
                      Ir para Sessão
                    </Button>
                  )}
                </div>
              </>
            )}

            <Separator />

            {/* Info Grid */}
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="flex items-center gap-2">
                <User className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-xs text-muted-foreground">Responsável</p>
                  <p className="font-medium">{getAssigneeName()}</p>
                </div>
              </div>

              {task.due_date && (
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-xs text-muted-foreground">Vencimento</p>
                    <p className="font-medium">
                      {safeFormat(parseDateSafe(task.due_date) || new Date(), 'dd/MM/yyyy', { locale: ptBR })}
                    </p>
                  </div>
                </div>
              )}

              {task.estimated_hours && (
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-xs text-muted-foreground">Duração Est.</p>
                    <p className="font-medium">{task.estimated_hours}h</p>
                  </div>
                </div>
              )}

              {task.case_id && (
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-xs text-muted-foreground">Case</p>
                    <Button
                      variant="link"
                      size="sm"
                      className="p-0 h-auto text-primary"
                      onClick={() => navigate(`/cases/${task.case_id}`)}
                    >
                      Ver Case
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {/* Contact & Deal Context */}
            {(task.contact_id || task.deal_id) && (
              <>
                <Separator />
                <div className="space-y-3 p-4 bg-muted/50 rounded-lg border">
                  <span className="text-xs font-semibold text-muted-foreground uppercase">
                    Cliente / Negociação
                  </span>

                  {task.contact_id && (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 min-w-0">
                        <User className="h-4 w-4 text-muted-foreground shrink-0" />
                        <div className="min-w-0">
                          <p className="text-sm font-medium truncate">
                            {[task.contact_first_name, task.contact_last_name].filter(Boolean).join(' ') || 'Contato'}
                          </p>
                          {task.contact_phone && (
                            <p className="text-xs text-muted-foreground flex items-center gap-1">
                              <Phone className="h-3 w-3" />
                              {task.contact_phone}
                            </p>
                          )}
                        </div>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        className="rounded-full shrink-0"
                        onClick={() => {
                          navigate(`/contactdetail?id=${task.contact_id}`);
                          onOpenChange(false);
                        }}
                      >
                        <ExternalLink className="h-3 w-3 mr-1" />
                        Contato
                      </Button>
                    </div>
                  )}

                  {task.deal_id && (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 min-w-0">
                        <Briefcase className="h-4 w-4 text-muted-foreground shrink-0" />
                        <p className="text-sm font-medium truncate">
                          {task.deal_name || 'Negociação'}
                        </p>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        className="rounded-full shrink-0"
                        onClick={() => {
                          navigate(`/dealdetail?id=${task.deal_id}`);
                          onOpenChange(false);
                        }}
                      >
                        <ExternalLink className="h-3 w-3 mr-1" />
                        Negociação
                      </Button>
                    </div>
                  )}
                </div>
              </>
            )}

            <Separator />

            {/* Editable Fields */}
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="status">Status</Label>
              <Select value={editedStatus} onValueChange={setEditedStatus} disabled={isCompleted}>
                  <SelectTrigger className="rounded-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                      <SelectItem key={key} value={key}>
                        {config.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="priority">Prioridade</Label>
              <Select value={editedPriority} onValueChange={setEditedPriority} disabled={isCompleted}>
                  <SelectTrigger className="rounded-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(PRIORITY_CONFIG).map(([key, config]) => (
                      <SelectItem key={key} value={key}>
                        {config.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="assignee">Responsável</Label>
              <Select value={editedAssignee} onValueChange={setEditedAssignee} disabled={isCompleted}>
                  <SelectTrigger className="rounded-full">
                    <SelectValue placeholder="Não atribuído" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={UNASSIGNED_VALUE}>Não atribuído</SelectItem>
                    {users
                      .sort((a, b) => {
                        const nameA = (a.display_name || a.email || '').toLowerCase();
                        const nameB = (b.display_name || b.email || '').toLowerCase();
                        return nameA.localeCompare(nameB);
                      })
                      .map((user) => (
                        <SelectItem key={user.id} value={user.id}>
                          {user.display_name || user.email}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="notes" className="flex items-center gap-2">
                  <MessageSquare className="h-4 w-4" />
                  Notas / Resposta
                </Label>
                <Textarea
                  id="notes"
                  value={editedNotes}
                  onChange={(e) => setEditedNotes(e.target.value)}
                  placeholder="Adicione notas ou resposta..."
                  rows={4}
                  className={cn("resize-none", isCompleted && "opacity-60 cursor-not-allowed")}
                  disabled={isCompleted}
                />
              </div>
            </div>

            <Separator />

            {/* Actions */}
            <div className="space-y-3">
              {hasChanges && !isCompleted && (
                <Button
                  onClick={handleSaveChanges}
                  className="w-full rounded-full"
                  disabled={isUpdating}
                >
                  {isUpdating ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Salvando...
                    </>
                  ) : (
                    'Salvar Alterações'
                  )}
                </Button>
              )}

              {task.status !== 'completed' && (
                <Button
                  variant="outline"
                  onClick={handleComplete}
                  className="w-full rounded-full border-emerald-500 text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-950/20"
                  disabled={isUpdating}
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  {isEscalation ? 'Completar com Resposta' : 'Marcar como Concluída'}
                </Button>
              )}

              <Button
                variant="outline"
                onClick={() => setShowDeleteDialog(true)}
                className="w-full rounded-full border-red-300 text-red-600 hover:bg-red-50 dark:hover:bg-red-950/20"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Excluir Tarefa
              </Button>
            </div>

            {/* Timestamps */}
            <div className="text-xs text-muted-foreground space-y-1 pt-4 border-t">
              <p>
                Criado por:{' '}
                {task.created_by_user_name || 
                  (task.created_by_first_name && task.created_by_last_name 
                    ? `${task.created_by_first_name} ${task.created_by_last_name}` 
                    : 'Sistema')}
              </p>
              <p>
                Criada em:{' '}
                {safeFormat(new Date(task.created_at), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
              </p>
              <p>
                Atualizada em:{' '}
                {safeFormat(new Date(task.updated_at), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
              </p>
              {task.completed_at && (
                <p>
                  Concluída em:{' '}
                  {safeFormat(new Date(task.completed_at), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                </p>
              )}
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir Tarefa</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir esta tarefa? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="rounded-full bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={isDeleting}
            >
              {isDeleting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Excluindo...
                </>
              ) : (
                'Excluir'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
